import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import type { LegalDoc } from "../data/legal";
interface LegalModalProps {
  doc: LegalDoc | null;
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const previous = document.activeElement as HTMLElement | null;
    const {
      overflow
    } = document.body.style;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-108" data-mp="1cw4y2dgy6xti">
      {doc && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/80 p-0 backdrop-blur-sm sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-109" data-mp="1ar81s5jcgl5i">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-title" className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-line bg-surface sm:rounded-2xl" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.25,
        ease
      }} onClick={event => event.stopPropagation()} data-id="element-110" data-mp="1pmxtmkb7nsi5">
            <div className="flex items-start justify-between gap-6 border-b border-line px-6 py-5 md:px-8" data-id="element-111" data-mp="e8aiw0qplm0n">
              <div data-id="element-112" data-mp="67cipqez18pc">
                <h2 id="legal-title" className="font-serif text-2xl text-cream md:text-3xl" data-id="element-113" data-mp="66t52lwct6h4">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs text-muted" data-id="element-114" data-mp="16x8h7pzkhbl">Última actualización: {doc.updated}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-muted transition-colors duration-150 hover:bg-ink hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-115" data-mp="aykyd46w9qay">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-116" data-mp="3odh7g400rfj3" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6 md:px-8" data-id="element-117" data-mp="18m31nzhse07k">
              <div className="space-y-6" data-id="element-118" data-mp="18kiamjuokzeg">
                {doc.sections.map(section => <section key={section.heading} data-id="element-119" data-mp="13myutfic4eke">
                    <h3 className="font-medium text-gold" data-id="element-120" data-mp="1b68315f3u77v">{section.heading}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-121" data-mp="1g5ui7kehtxjb">{section.body}</p>
                  </section>)}
              </div>
            </div>

            <div className="border-t border-line px-6 py-4 md:px-8" data-id="element-122" data-mp="1285wybgikodq">
              <button type="button" onClick={onClose} className="w-full rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-gold-light focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:w-auto" data-id="element-123" data-mp="168g0lhgu32pf">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}